import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Logo from '../components/common/Logo';
import { Map, Bell, Shield, ArrowRight, AlertTriangle, Users, MessageSquare, Lock } from 'lucide-react';

const LandingPage = () => {
  const publicFeatures = [
    {
      icon: Map,
      title: 'Live Emergency Map',
      description: 'Real-time view of road closures, detours, parade routes, and closed areas across the county.',
      color: 'bg-blue-500',
      link: '/map',
    },
    {
      icon: Bell,
      title: 'Public Alerts',
      description: 'View all emergency notifications and road conditions affecting your area.',
      color: 'bg-orange-500',
      link: '/alerts',
    },
  ];

  const personnelFeatures = [
    {
      icon: MessageSquare,
      title: 'Personnel Communication',
      description: 'Secure inter-department chat system for emergency responders with read receipts.',
      color: 'bg-green-500',
    },
    {
      icon: Shield,
      title: 'Admin Control Center',
      description: 'Manage road closures, activate alerts, and coordinate emergency response in real-time.',
      color: 'bg-red-500',
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1610725818584-cbca8e5432b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb250YW5hJTIwbGFuZHNjYXBlJTIwbW91bnRhaW5zJTIwcm9hZHxlbnwwfHx8fDE3NjkxMTQ4NjZ8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/70 to-zinc-950" />

        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
          <Logo size="lg" linkTo="/" />
          <div className="flex items-center gap-4">
            <Link to="/map">
              <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="nav-public-map">
                <Map className="w-4 h-4 mr-2" />
                View Map
              </Button>
            </Link>
            <Link to="/alerts">
              <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="nav-public-alerts">
                <Bell className="w-4 h-4 mr-2" />
                Alerts
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500" data-testid="nav-login">
                <Lock className="w-4 h-4 mr-2" />
                Personnel
              </Button>
            </Link>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-red-500/20 text-red-400 border border-red-500/30 px-4 py-1.5">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Emergency Services Portal
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-headings text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight mb-6"
          >
            Stay <span className="text-red-500">Informed.</span>
            <br />
            Stay <span className="text-blue-500">Safe.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-zinc-400 max-w-2xl mb-10 font-body"
          >
            Real-time emergency information, road closures, detours, and alerts for Anaconda-Deer Lodge County residents and visitors.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/map">
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 text-lg font-bold uppercase tracking-wide shadow-lg shadow-red-500/30 group" data-testid="hero-view-map">
                <Map className="w-5 h-5 mr-2" />
                View Live Map
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/alerts">
              <Button size="lg" variant="outline" className="border-zinc-600 text-white px-8 py-6 text-lg font-bold uppercase tracking-wide hover:bg-zinc-800" data-testid="hero-view-alerts">
                <Bell className="w-5 h-5 mr-2" />
                View Alerts
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Public Section */}
      <section className="py-24 px-6 lg:px-12 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
              <Users className="w-4 h-4 mr-2" />
              Public Access
            </Badge>
            <h2 className="font-headings text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4">
              For Residents & Visitors
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              No account required. Access real-time emergency information anytime.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {publicFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={feature.link}>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-zinc-600 transition-all card-hover group cursor-pointer h-full">
                      <div className={`w-14 h-14 ${feature.color} rounded-lg flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="font-headings text-2xl font-bold uppercase tracking-wide mb-3">{feature.title}</h3>
                      <p className="text-zinc-400 leading-relaxed mb-4">{feature.description}</p>
                      <span className="inline-flex items-center text-blue-400 font-medium group-hover:text-blue-300">
                        Access Now
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personnel Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-red-500/20 text-red-400 border-red-500/30">
              <Shield className="w-4 h-4 mr-2" />
              Personnel Access
            </Badge>
            <h2 className="font-headings text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4">
              For Emergency Personnel
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Secure access for authorized emergency services staff.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            {personnelFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 opacity-80"
                >
                  <div className={`w-14 h-14 ${feature.color} rounded-lg flex items-center justify-center mb-6 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-headings text-2xl font-bold uppercase tracking-wide mb-3">{feature.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
                  <div className="mt-4 flex items-center text-zinc-500">
                    <Lock className="w-4 h-4 mr-2" />
                    <span className="text-sm">Requires authentication</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/login">
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white px-8 font-bold uppercase" data-testid="personnel-login-btn">
                <Lock className="w-5 h-5 mr-2" />
                Personnel Login
              </Button>
            </Link>
            <p className="text-zinc-500 text-sm mt-4">
              New personnel? Contact your department administrator for account access.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size="sm" showText={false} linkTo="/" />
            <span className="text-sm text-zinc-500">© 2024 Anaconda-Deer Lodge County Emergency Services</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/map" className="hover:text-white transition-colors">Public Map</Link>
            <Link to="/alerts" className="hover:text-white transition-colors">Public Alerts</Link>
            <span className="text-zinc-700">|</span>
            <Link to="/login" className="hover:text-white transition-colors">Personnel Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
